/**
 * Dynamic Brand & Tenant Detection Helper
 * Automatically sets the Tab Title to exactly match the Domain Name:
 * - On Live Domains: Uses window.location.hostname (e.g. crm.codexacorn.com, crm.aarogyaindia.com, etc.)
 * - On Localhost: Extracts the domain from REACT_APP_API_BASE_URL
 */

import { BASE_URL } from '../services/api';

export const getDomainName = () => {
  if (typeof window === 'undefined') {
    return 'crm.codexacorn.com';
  }

  const hostname = window.location.hostname;

  // 1. If running on a live domain, use the exact hostname
  if (hostname && hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.startsWith('192.168.')) {
    return hostname;
  }

  // 2. If running on localhost / dev, extract domain from API Base URL
  const apiBaseUrl = BASE_URL || process.env.REACT_APP_API_BASE_URL || '';
  try {
    const urlObj = new URL(apiBaseUrl.startsWith('http') ? apiBaseUrl : `https://${apiBaseUrl}`);
    if (urlObj.hostname && urlObj.hostname !== 'localhost' && urlObj.hostname !== '127.0.0.1') {
      return urlObj.hostname;
    }
  } catch (e) {
    const match = apiBaseUrl.match(/https?:\/\/([^/:]+)/);
    if (match && match[1]) {
      return match[1];
    }
  }

  return 'crm.codexacorn.com';
};

/**
 * Extracts a formatted, human-readable Brand Name based on the current domain / base URL
 */
export const getBrandName = () => {
  if (process.env.REACT_APP_BRAND_NAME) {
    return process.env.REACT_APP_BRAND_NAME;
  }

  const domain = getDomainName().toLowerCase();

  // Explicit known brand overrides
  if (domain.includes('codexacorn') || domain.includes('codexcorn')) {
    return 'Codex Acorn';
  }
  if (domain.includes('aarogya') || domain.includes('crmaarogyaindia')) {
    return 'Aarogya India';
  }
  if (domain.includes('indhara')) {
    return 'Indhara';
  }

  // Smart fallback: strip common prefixes and TLDs
  const cleanDomain = domain
    .replace(/^(https?:\/\/)?(www\.|crm\.|api\.|app\.|admin\.)/i, '')
    .split('.')[0];

  if (cleanDomain) {
    // Convert kebab/snake case or camel-friendly to capitalized words
    const words = cleanDomain.split(/[-_]+/).filter(Boolean);
    if (words.length > 0) {
      return words
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    }
    return cleanDomain.charAt(0).toUpperCase() + cleanDomain.slice(1);
  }

  return 'Codex Acorn';
};

/**
 * Returns formatted company name for footer / legal text
 */
export const getCompanyName = () => {
  const brandName = getBrandName();
  if (brandName.toLowerCase().includes('aarogya')) {
    return 'LM25 Aarogya India Private Limited';
  }
  return `${brandName} Private Limited`;
};

export const getBrandInfo = () => {
  const domain = getDomainName();
  const brandName = getBrandName();
  const companyName = getCompanyName();
  return {
    brandName,
    companyName,
    domain,
    tabTitle: domain,
  };
};

/**
 * Dynamically updates document.title to the exact domain name
 */
export const updateDocumentBrand = () => {
  if (typeof document !== 'undefined') {
    const domain = getDomainName();
    document.title = domain;
  }
};

